<template>
  <div class="landing status-page">
    <header class="landing-nav">
      <div class="landing-nav__inner">
        <router-link
          to="/"
          class="landing-nav__brand"
          aria-label="AGVA home"
        >
          <img
            class="landing-nav__logo"
            :src="companyLogoImage"
            alt="AGVA"
          >
        </router-link>
        <router-link
          to="/"
          class="status-page__back"
        >
          Back to home
        </router-link>
      </div>
    </header>

    <main class="status-page__main">
      <div class="status-page__header">
        <h1>System status</h1>
        <p>AGVA platform availability and response times</p>
      </div>

      <div class="status-grid status-grid--3">
        <article class="status-card">
          <h2 class="status-card__label">Current status</h2>
          <p class="status-card__value status-card__value--ok">{{ snapshot.status }}</p>
          <p class="status-card__meta">{{ snapshot.uptimeLabel }}</p>
        </article>

        <article class="status-card">
          <h2 class="status-card__label">Last check</h2>
          <p class="status-card__value">{{ snapshot.lastCheckLabel }}</p>
          <p class="status-card__meta">{{ snapshot.checkIntervalLabel }}</p>
        </article>

        <article class="status-card">
          <h2 class="status-card__label">Last 24 hours</h2>
          <p class="status-card__value status-card__value--ok">{{ snapshot.last24Hours.uptime }}</p>
          <p class="status-card__meta">{{ snapshot.last24Hours.detail }}</p>
        </article>
      </div>

      <article class="status-card status-card--chart">
        <div class="status-card__chart-head">
          <h2 class="status-card__title">Response time</h2>
          <label class="status-range">
            <span class="sr-only">Time range</span>
            <select
              v-model="selectedRange"
              class="status-range__select"
              @change="refreshSnapshot"
            >
              <option
                v-for="option in snapshot.rangeOptions"
                :key="option.key"
                :value="option.key"
              >
                {{ option.label }}
              </option>
            </select>
          </label>
        </div>

        <VueApexCharts
          type="line"
          height="280"
          :options="chartOptions"
          :series="chartSeries"
        />

        <div class="status-summary">
          <div>
            <span class="status-summary__label">Average</span>
            <strong>{{ formatMs(snapshot.chart.average) }}</strong>
          </div>
          <div>
            <span class="status-summary__label">Minimum</span>
            <strong>{{ formatMs(snapshot.chart.minimum) }}</strong>
          </div>
          <div>
            <span class="status-summary__label">Maximum</span>
            <strong>{{ formatMs(snapshot.chart.maximum) }}</strong>
          </div>
        </div>
      </article>

      <p class="status-page__blurb">
        {{ snapshot.blurb }}
      </p>

      <div class="status-grid status-grid--3">
        <article
          v-for="period in snapshot.periodCards"
          :key="period.key"
          class="status-card"
        >
          <h2 class="status-card__label">{{ period.label }}</h2>
          <p class="status-card__value status-card__value--ok">{{ period.uptime }}</p>
          <p class="status-card__meta">{{ period.detail }}</p>
        </article>
      </div>
    </main>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import { $themeConfig } from '@themeConfig'
import { getSystemStatusSnapshot } from '@/libs/systemStatus'

export default {
  name: 'SystemStatus',
  components: {
    VueApexCharts,
  },
  data() {
    const { companyLogoImage } = $themeConfig.app
    return {
      companyLogoImage,
      selectedRange: '30d',
      snapshot: getSystemStatusSnapshot(new Date(), '30d'),
      refreshTimer: null,
    }
  },
  computed: {
    chartSeries() {
      return [
        {
          name: 'Response time',
          data: this.snapshot.chart.values,
        },
      ]
    },
    chartOptions() {
      return {
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: 'Arial, Helvetica, sans-serif',
        },
        colors: ['#4f439e'],
        stroke: {
          curve: 'smooth',
          width: 2.5,
        },
        dataLabels: { enabled: false },
        grid: {
          borderColor: 'rgba(79, 67, 158, 0.14)',
          strokeDashArray: 4,
        },
        xaxis: {
          categories: this.snapshot.chart.categories,
          labels: {
            rotate: -35,
            style: {
              colors: '#5c5678',
              fontSize: '11px',
            },
            hideOverlappingLabels: true,
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            formatter: (value) => `${Math.round(value)}ms`,
            style: {
              colors: '#5c5678',
              fontSize: '11px',
            },
          },
        },
        tooltip: {
          y: {
            formatter: (value) => `${Number(value).toFixed(2)} ms`,
          },
        },
        legend: { show: false },
      }
    },
  },
  mounted() {
    this.refreshTimer = setInterval(() => {
      this.refreshSnapshot()
    }, 15000)
  },
  beforeUnmount() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
      this.refreshTimer = null
    }
  },
  methods: {
    refreshSnapshot() {
      this.snapshot = getSystemStatusSnapshot(new Date(), this.selectedRange)
    },
    formatMs(value) {
      return `${Number(value).toFixed(2)} ms`
    },
  },
}
</script>

<style scoped>
.landing {
  --landing-accent: #4f439e;
  --landing-accent-deep: #3b3278;
  --landing-accent-soft: #ebe7f8;
  --landing-ink: #1c1833;
  --landing-muted: #5c5678;
  --landing-line: rgba(79, 67, 158, 0.22);
  --landing-surface: #f4f2fb;
  --landing-white: #ffffff;
  --landing-ok: #1f8a4c;
  --landing-display: Arial, Helvetica, sans-serif;
  --landing-body: Arial, Helvetica, sans-serif;

  min-height: 100vh;
  color: var(--landing-ink);
  font-family: var(--landing-body);
  background:
    radial-gradient(1200px 600px at 12% -10%, rgba(79, 67, 158, 0.18), transparent 60%),
    radial-gradient(900px 500px at 100% 0%, rgba(79, 67, 158, 0.12), transparent 55%),
    linear-gradient(180deg, #faf9ff 0%, var(--landing-surface) 40%, #eeeaf8 100%);
}

.landing-nav {
  position: sticky;
  top: 0;
  z-index: 50;
  backdrop-filter: blur(12px);
  background: rgba(250, 249, 255, 0.86);
  border-bottom: 1px solid var(--landing-line);
}

.landing-nav__inner {
  max-width: 1120px;
  margin: 0 auto;
  padding: 0.75rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.landing-nav__brand {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.landing-nav__logo {
  height: 36px;
  width: auto;
  display: block;
}

.status-page__back {
  color: var(--landing-accent);
  font-weight: 600;
  text-decoration: none;
}

.status-page__back:hover {
  color: var(--landing-accent-deep);
}

.status-page__main {
  max-width: 1120px;
  margin: 0 auto;
  padding: 2rem 1.25rem 3.5rem;
}

.status-page__header {
  margin-bottom: 1.5rem;
}

.status-page__header h1 {
  margin: 0 0 0.35rem;
  font-family: var(--landing-display);
  font-size: clamp(1.6rem, 3vw, 2.1rem);
  font-weight: 700;
  letter-spacing: -0.03em;
}

.status-page__header p {
  margin: 0;
  color: var(--landing-muted);
}

.status-grid {
  display: grid;
  gap: 1rem;
}

.status-grid--3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.status-card {
  background: var(--landing-white);
  border: 1px solid var(--landing-line);
  border-radius: 0.85rem;
  padding: 1.15rem 1.2rem;
  box-shadow: 0 10px 28px rgba(28, 24, 51, 0.04);
}

.status-card--chart {
  margin: 1rem 0;
}

.status-card__label {
  margin: 0;
  font-size: 0.92rem;
  font-weight: 500;
  color: var(--landing-muted);
}

.status-card__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
}

.status-card__value {
  margin: 0.45rem 0 0.35rem;
  font-size: clamp(1.55rem, 3vw, 2rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.15;
}

.status-card__value--ok {
  color: var(--landing-ok);
}

.status-card__meta {
  margin: 0;
  color: var(--landing-muted);
  font-size: 0.92rem;
}

.status-card__chart-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.status-range__select {
  appearance: none;
  border: 1px solid var(--landing-line);
  background: var(--landing-surface);
  color: var(--landing-ink);
  border-radius: 0.5rem;
  padding: 0.45rem 2rem 0.45rem 0.75rem;
  font-size: 0.9rem;
  font-weight: 500;
  background-image: linear-gradient(45deg, transparent 50%, var(--landing-muted) 50%),
    linear-gradient(135deg, var(--landing-muted) 50%, transparent 50%);
  background-position: calc(100% - 14px) 55%, calc(100% - 9px) 55%;
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
  cursor: pointer;
}

.status-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid var(--landing-line);
}

.status-summary__label {
  display: block;
  color: var(--landing-muted);
  font-size: 0.85rem;
  margin-bottom: 0.2rem;
}

.status-page__blurb {
  margin: 0.25rem 0 1.25rem;
  color: var(--landing-muted);
  font-size: 0.95rem;
  line-height: 1.5;
  max-width: 52rem;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 800px) {
  .status-grid--3,
  .status-summary {
    grid-template-columns: 1fr;
  }

  .status-card__chart-head {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
